import config from '@config'
import RendererContext, {
  PageTypeSpecificJSONRendererProps,
} from '@contexts/renderer'
import { PageData } from '@hooks/usePageMetadata'
import { defaultConfig as queryConfig } from '@utils/react-query'
import cookie from 'cookie'
import { GetServerSideProps } from 'next'
import { ComponentType, FunctionComponent, useMemo } from 'react'
import {
  dehydrate,
  DehydratedState,
  QueryClient,
  useQueryClient,
} from 'react-query'
import {
  extractPageType,
  fetchdlApi,
  fetchPage,
  fetchSkeleton,
  getFullPageUrl,
  isValidPageMetadata,
  isValidSkeletonData,
  PageType,
  getRedirectToRSSInfo,
  ValidSkeletonData,
  getRSSContentInfo,
  RSSPathType,
} from '@utils/cook'
import JSONRenderer from '@utils/JSONRenderer'
import MainWrapper from '@components/MainWrapper'
import { getKropkaConfigObject } from '@utils/kropka'
import { tranformDataforRSS } from '@utils/rss'

export interface CatchAllPageServerProps {
  isError: boolean
  dehydratedQueryState: DehydratedState
  documentProps?: {
    atmEnv: string
    atmVersion: string
    dlApiScriptContents?: string
    disableThirdPartyScripts: boolean
    kropkaConfig: string
  }
}

const {
  backend: { env },
  ads: {
    admeira: {
      appnexus: {
        environment: atmConfigFileEnv,
        version: atmConfigFileVersion,
      },
    },
  },
} = config

const getRootComponents = (pageType: PageType) => {
  let PageTypeSpecificJSONRenderer: FunctionComponent<PageTypeSpecificJSONRendererProps>

  switch (pageType) {
    case 'sectionToi': {
      PageTypeSpecificJSONRenderer = function PageTypeSpecificJSONRenderer({
        children,
      }) {
        return <JSONRenderer pageType="sectionToi">{children}</JSONRenderer>
      }
      break
    }
    case 'article': {
      PageTypeSpecificJSONRenderer = function PageTypeSpecificJSONRenderer({
        children,
      }) {
        return <JSONRenderer pageType="article">{children}</JSONRenderer>
      }
      break
    }
    case 'section':
    default: {
      PageTypeSpecificJSONRenderer = function PageTypeSpecificJSONRenderer({
        children,
      }) {
        return <JSONRenderer pageType="section">{children}</JSONRenderer>
      }
      break
    }
  }

  return { PageTypeSpecificJSONRenderer }
}

const CatchAllPage: FunctionComponent = () => {
  const queryClient = useQueryClient()

  const pageData = queryClient.getQueryData<PageData>('page')

  const pageType = extractPageType(pageData?.metadata)

  const { PageTypeSpecificJSONRenderer } = useMemo<{
    PageTypeSpecificJSONRenderer: ComponentType<PageTypeSpecificJSONRendererProps>
  }>(() => getRootComponents(pageType), [pageType])

  return (
    <RendererContext.Provider value={PageTypeSpecificJSONRenderer}>
      <RendererContext.Consumer>
        {(PageTypeSpecificJSONRenderer) =>
          PageTypeSpecificJSONRenderer ? (
            <MainWrapper>
              <PageTypeSpecificJSONRenderer>
                {[
                  ...(pageData?.children ?? []),
                  { kind: [`load-ads-${pageType}`] },
                ]}
              </PageTypeSpecificJSONRenderer>
            </MainWrapper>
          ) : null
        }
      </RendererContext.Consumer>
    </RendererContext.Provider>
  )
}

export default CatchAllPage

export const getServerSideProps: GetServerSideProps<
  CatchAllPageServerProps
> = async (context) => {
  const queryClient = new QueryClient(queryConfig)

  const fullPageUrl = getFullPageUrl(context.query.pageUrl ?? '')
  const token = context.query.token
  const isTeaserPreviewPage = context.query.layout === 'teaser-preview'

  const { shouldRedirect, redirectPath } = getRedirectToRSSInfo(
    context?.req?.url ?? ''
  )

  if (shouldRedirect) {
    return {
      redirect: {
        destination: redirectPath,
        permanent: false,
      },
    }
  }

  const { shouldServeRSSCONTENT, rssPath, rssRequestUrl } = getRSSContentInfo(
    context?.req?.url ?? ''
  )

  if (shouldServeRSSCONTENT) {
    // Fetch the RSS Data
    const pageDataForRss = await fetchPage({
      pageUrl: rssRequestUrl,
      token: (token as string) ?? '',
      foxCookies: {},
      isTeaserPreviewPage: false,
    })

    if (!pageDataForRss || Object.keys(pageDataForRss).length === 0) {
      return {
        notFound: true,
      }
    }

    const feedRSS = tranformDataforRSS({
      data: pageDataForRss,
      feedType: rssPath as RSSPathType,
    })

    context.res.setHeader('Content-Type', 'text/xml')
    context.res.write(feedRSS)
    context.res.end()

    return {
      props: {} as CatchAllPageServerProps,
    }
  }

  const isServerRendered = !context?.req?.url?.startsWith('/_next/data/')

  const {
    ADM_TAG_MANAGER_ENV,
    ADM_TAG_MANAGER_VERSION,
    au_seg,
    ea_uuid,
  }: {
    ADM_TAG_MANAGER_ENV?: string
    ADM_TAG_MANAGER_VERSION?: string
    au_seg?: string
    ea_uuid?: string
  } = cookie.parse(context?.req?.headers?.cookie ?? '')

  const atmEnv = ADM_TAG_MANAGER_ENV || atmConfigFileEnv
  const atmVersion = ADM_TAG_MANAGER_VERSION || atmConfigFileVersion

  let gdprApplies = false
  let disableThirdPartyScripts = false

  if (isServerRendered) {
    // This is used for the consent management overlay.
    // We check if the user is coming from Switzerland which doesn't fall under GDPR regulations
    // and so no consent overlay needs to be shown
    // Also no consent overlay should be shown when an e2e test is running
    // Lastly, the x-bl-g header is only supported where AKAMAI is supported,
    // on production and staging environments, so for local and PR environments
    // we set the gdprApplies to false.
    gdprApplies =
      ['prod', 'stg'].includes(env) && !context?.req?.headers?.['x-bl-g']

    // Send the header as a response header back to the server (for testing purposes)
    context?.res?.setHeader(
      'x-bl-g-check',
      `${context?.req?.headers?.['x-bl-g']}`
    )

    queryClient.setQueryData('gdprApplies', gdprApplies)
  } else {
    queryClient.removeQueries('gdprApplies', { exact: true })
  }

  await Promise.allSettled([
    isServerRendered
      ? queryClient.prefetchQuery('skeleton', fetchSkeleton, {
          //! Skeleton is shared for all users, but we also fetch
          //! it every time we hit the Node endpoint, because we
          //! want to have the latest version to be in cache.
          staleTime: 0,
        })
      : new Promise((resolve) => {
          queryClient.removeQueries('skeleton', { exact: true })
          resolve(true)
        }),
    queryClient.prefetchQuery(
      'page',
      () =>
        fetchPage({
          pageUrl: fullPageUrl,
          token: (token as string) ?? '',
          foxCookies: { au_seg, ea_uuid },
          isTeaserPreviewPage,
        }),
      {
        //! Page data is *not* shared for all users and depends on
        //! user segment, and other things that *only AKAMAI* knows about.
        //! Thus, we need to fetch it *every single time* we hit the Node endpoint.
        staleTime: 0,
      }
    ),
    isServerRendered
      ? queryClient.prefetchQuery('dlApi', fetchdlApi, {
          //! dlApi is shared for all users and doesn't need
          //! to be fetched more frequent that once every 10 minutes.
          staleTime: 10 * 60 * 1000,
        })
      : new Promise((resolve) => {
          queryClient.removeQueries('dlApi', { exact: true })
          resolve(true)
        }),
  ])

  queryClient.setQueryData('force-center-content', false)
  queryClient.setQueryData('full-screen-portal-rendered-node-ref', null)
  queryClient.setQueryData('full-screen-image-shown', false)
  queryClient.setQueryData('aureusOfferTracked', false)
  queryClient.setQueryData('aureusRecommendationOfferTracked', false)
  queryClient.setQueryData('isServerRendered', isServerRendered)
  queryClient.setQueryData('areAdSlotsLoaded', false)
  queryClient.setQueryData('RiAdTagManagerInitCode', '')
  queryClient.setQueryData('burgerMenuVisibility', {
    isVisible: false,
    resetScrollPosition: true,
  })
  queryClient.setQueryData('userMenuVisible', false)
  queryClient.setQueryData('articleSchemaData', {
    articleBody: [],
    description: '',
  })

  if (context.query.utm_source) {
    queryClient.setQueryData('utmSource', context.query.utm_source)
  } else {
    queryClient.removeQueries('utmSource', { exact: true })
  }
  if (context.query.utm_medium) {
    queryClient.setQueryData('utmMedium', context.query.utm_medium)
  } else {
    queryClient.removeQueries('utmMedium', { exact: true })
  }
  if (context.query.utm_campaign) {
    queryClient.setQueryData('utmCampaign', context.query.utm_campaign)
  } else {
    queryClient.removeQueries('utmCampaign', { exact: true })
  }

  const dlApiScriptContents = queryClient.getQueryData<string>('dlApi')
  const skeletonData = queryClient.getQueryData<ValidSkeletonData | unknown>(
    'skeleton'
  )

  if (isServerRendered) {
    if (!isValidSkeletonData(skeletonData)) {
      return {
        notFound: true,
      }
    }

    queryClient.setQueryData(
      'navigationSkeleton',
      skeletonData.skeletonData[0].navigation
    )
    queryClient.setQueryData(
      'footerSkeleton',
      skeletonData.skeletonData[0].footer
    )
    queryClient.setQueryData(
      'videoSkeleton',
      skeletonData.skeletonData[0].video
    )

    //! We never actually use skeleton as is,
    //! so we remove it from the cache.
    queryClient.removeQueries('skeleton', { exact: true })

    disableThirdPartyScripts =
      !!skeletonData.skeletonData[0].disableThirdPartyScripts

    queryClient.setQueryData(
      'disableThirdPartyScripts',
      disableThirdPartyScripts
    )
  } else {
    queryClient.removeQueries('navigationSkeleton', { exact: true })
    queryClient.removeQueries('footerSkeleton', { exact: true })
    queryClient.removeQueries('videoSkeleton', { exact: true })
    queryClient.removeQueries('disableThirdPartyScripts', { exact: true })
  }

  const pageData = queryClient.getQueryData<any>('page')

  if (pageData?.statusCode === 301 && !!pageData?.location) {
    return {
      redirect: {
        destination: (pageData as any).location,
        permanent: true,
      },
    }
  }

  if (pageData?.statusCode === 404) {
    return {
      notFound: true,
    }
  }

  if (!isValidPageMetadata(pageData?.metadata)) {
    return {
      notFound: true,
    }
  }

  let documentProps

  if (isServerRendered) {
    documentProps = {
      atmEnv,
      atmVersion,
      dlApiScriptContents,
      disableThirdPartyScripts,
      kropkaConfig: JSON.stringify(
        getKropkaConfigObject({
          metadata: pageData.metadata,
          gdprApplies,
        })
      ),
    }
  }

  return {
    props: {
      isError: false,
      dehydratedQueryState: dehydrate(queryClient),
      //! "documentProps" is meant to be used *only* by _document.tsx.
      //! For anything else use react-query.
      ...(documentProps ? { documentProps } : {}),
    },
  }
}
